import api from "@/libs/axios";
import Cookie from "js-cookie";

const AFFILIATE_STATUS_ACTIVE = "active";

const normalizeAffiliateStatus = (status) =>
    String(status || "")
        .trim()
        .toLowerCase();

const isAffiliateStatusBlocked = (status) => {
    const normalized = normalizeAffiliateStatus(status);
    return normalized.length > 0 && normalized !== AFFILIATE_STATUS_ACTIVE;
};

const normalizeAffiliateProfileErrorMessage = (message) =>
    String(message || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();

const isAffiliateProfileMissingMessage = (message) => {
    const normalized = normalizeAffiliateProfileErrorMessage(message);
    return normalized.includes("perfil de afiliado nao encontrado")
        || normalized.includes("nao encontrado para esta loja");
};

const isAffiliateProfileBlockedMessage = (message) => {
    const normalized = normalizeAffiliateProfileErrorMessage(message);
    return normalized.includes("bloquead")
        || normalized.includes("suspend")
        || normalized.includes("inativ");
};

const persistToken = (token) => {
    if (!token) return;
    Cookie.set("oliveiraToken", token, { expires: 7 });
    if (typeof localStorage !== "undefined") {
        localStorage.setItem("oliveiraToken", token);
    }
};

const clearToken = () => {
    Cookie.remove("oliveiraToken");
    if (typeof localStorage !== "undefined") {
        localStorage.removeItem("oliveiraToken");
    }
};

const normalizeUserRoles = (roles = []) => {
    const normalizedRoles = Array.isArray(roles) ? [...roles] : [];
    const permissionRoles = [];
    const existingRoleKeys = new Set();

    normalizedRoles.forEach((role) => {
        if (!role?.action || !role?.subject) return;
        existingRoleKeys.add(`${role.action}:${role.subject}`);
    });

    normalizedRoles.forEach((role) => {
        if (!Array.isArray(role?.permissions)) return;
        role.permissions.forEach((permission) => {
            if (!permission?.name) return;
            const permissionKey = `read:${permission.name}`;
            if (existingRoleKeys.has(permissionKey)) return;
            existingRoleKeys.add(permissionKey);
            permissionRoles.push({
                action: "read",
                subject: permission.name,
            });
        });
    });

    const publicRoleExists = normalizedRoles.some((role) => role?.action === "read" && role?.subject === "Public")
        || permissionRoles.some((role) => role?.action === "read" && role?.subject === "Public");

    if (!publicRoleExists) {
        permissionRoles.push({
            action: "read",
            subject: "Public",
        });
    }

    return [...normalizedRoles, ...permissionRoles];
};

const normalizeAuthUser = (user) => {
    if (!user || typeof user !== "object" || Array.isArray(user)) return null;

    return {
        ...user,
        roles: normalizeUserRoles(user.roles),
        is_affiliate: Boolean(user.is_affiliate),
    };
};

const getObjectCandidate = (candidates = []) =>
    candidates.find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate)) || null;

const getTokenCandidate = (candidates = []) =>
    candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length > 0) || "";

const resolveAffiliateOnboardingResponse = (payload = {}) => {
    const dataNode = payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
        ? payload.data
        : {};

    return {
        token: getTokenCandidate([
            payload?.token,
            payload?.access_token,
            dataNode?.token,
            dataNode?.access_token,
        ]),
        user: getObjectCandidate([
            payload?.users,
            payload?.user,
            dataNode?.users,
            dataNode?.user,
        ]),
        affiliateAccount: getObjectCandidate([
            payload?.affiliate_account,
            payload?.account,
            dataNode?.affiliate_account,
            dataNode?.account,
        ]),
        affiliateProfile: getObjectCandidate([
            payload?.affiliate_profile,
            payload?.profile,
            payload?.affiliate_store_profile,
            dataNode?.affiliate_profile,
            dataNode?.profile,
            dataNode?.affiliate_store_profile,
        ]),
        affiliateStoreProfiles: Array.isArray(payload?.affiliate_store_profiles)
            ? payload.affiliate_store_profiles
            : Array.isArray(dataNode?.affiliate_store_profiles)
                ? dataNode.affiliate_store_profiles
                : null,
    };
};

const authentication = {
    state: {
        user: [],
        userLocale: localStorage.getItem('psiqueasy-locale') || 'en',
        isAffiliate: false,
        affiliateAccount: null,
        affiliateStoreProfiles: [],
        affiliateProfile: null,
        affiliateProfileStatus: null,
        affiliateProfileCheckedAt: null,
    },
    getters: {
        returnUser: state => state.user,
        returnLocale: state => state.userLocale,
        returnIsAffiliate: state => state.isAffiliate,
        returnAffiliateAccount: state => state.affiliateAccount,
        returnAffiliateStoreProfiles: state => state.affiliateStoreProfiles,
        returnAffiliateProfile: state => state.affiliateProfile,
        returnAffiliateProfileStatus: state => state.affiliateProfileStatus,
        returnAffiliateProfileCheckedAt: state => state.affiliateProfileCheckedAt,
    },
    actions: {
        authenticateUser: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {
                await api.post('/login', data).then(response => {
                    persistToken(response.data.token);
                    if (['admin', 'merchant'].includes(response.data.users.level)) {
                        window.location.href = `${process.env.VUE_APP_FRONT_URL.replace(
                            "app",
                            "admin"
                        )}`;
                    } else {
                        const normalizedUser = normalizeAuthUser(response.data.users)
                        commit("addUser", { dataUser: normalizedUser });

                        resolve(response)
                    }
                }).catch(error => {

                    reject(error.response.data)
                })
            });
        },
        activateAffiliate: ({ dispatch }, payload = {}) => {
            // legado: manter para compatibilidade, agora delega para onboardAffiliate
            return dispatch('onboardAffiliate', payload)
        },
        onboardAffiliate: ({ state, commit, dispatch }, payload = {}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const userId = state.user?.id
                    if (!userId) {
                        return reject('Usuário não autenticado.')
                    }

                    const body = {
                        store_ids: payload.store_ids || [],
                        pix_key: payload.pix_key,
                        pix_beneficiary_name: payload.pix_beneficiary_name,
                        bank_data: payload.bank_data,
                        notes: payload.notes,
                        method: payload.method,
                        payment_method: payload.method,
                        accept_terms: payload.accept_terms ? 1 : 0,
                        terms: payload.accept_terms ? 1 : 0,
                    }

                    const { data } = await api.post(`/affiliate/onboarding`, body)
                    const onboardingResponse = resolveAffiliateOnboardingResponse(data)

                    console.log('[affiliate/onboarding] success', {
                        userId,
                        hasAccountPayload: Boolean(onboardingResponse.affiliateAccount),
                        hasProfilePayload: Boolean(onboardingResponse.affiliateProfile),
                        hasUserPayload: Boolean(onboardingResponse.user),
                        hasTokenPayload: Boolean(onboardingResponse.token),
                    })

                    if (onboardingResponse.token) {
                        persistToken(onboardingResponse.token)
                    }

                    const normalizedResponseUser = normalizeAuthUser(onboardingResponse.user)
                    const updatedUser = normalizeAuthUser({
                        ...(state.user || {}),
                        ...(normalizedResponseUser || {}),
                        roles: Array.isArray(normalizedResponseUser?.roles) && normalizedResponseUser.roles.length
                            ? normalizedResponseUser.roles
                            : state.user?.roles,
                        is_affiliate: true,
                        affiliate_account: onboardingResponse.affiliateAccount
                            || normalizedResponseUser?.affiliate_account
                            || state.user?.affiliate_account
                            || null,
                        affiliate_profile: onboardingResponse.affiliateProfile
                            || normalizedResponseUser?.affiliate_profile
                            || state.user?.affiliate_profile
                            || null,
                        affiliate_store_profiles: onboardingResponse.affiliateStoreProfiles
                            || normalizedResponseUser?.affiliate_store_profiles
                            || state.user?.affiliate_store_profiles
                            || [],
                    })
                    commit("addUser", { dataUser: updatedUser });

                    if (onboardingResponse.affiliateProfile) {
                        commit('setAffiliateProfileState', {
                            profile: onboardingResponse.affiliateProfile,
                            checkedAt: Date.now(),
                        })
                    }

                    // O backend pode responder sem token novo logo após o onboarding.
                    // Nesse caso mantemos a sessão atual e evitamos logout forçado.
                    await dispatch('recoverUserData', {
                        skipForceLogoutOn401: true,
                    }).catch((error) => {
                        console.warn('[affiliate/onboarding] recoverUserData skipped', error?.response?.status || error?.message || error)
                    })

                    resolve(data)
                } catch (error) {
                    reject(error.response?.data || error.message)
                }
            })
        },
        fetchAffiliateStoreProfile: ({ state, commit }, payload = {}) => {
            return new Promise(async (resolve, reject) => {
                const force = Boolean(payload.force)
                const maxAgeMs = Number.isFinite(Number(payload.maxAgeMs)) ? Number(payload.maxAgeMs) : 60000
                const now = Date.now()

                if (
                    !force &&
                    state.affiliateProfileCheckedAt &&
                    now - Number(state.affiliateProfileCheckedAt) < maxAgeMs
                ) {
                    return resolve(state.affiliateProfile)
                }

                const storeNick = payload.storeNick || process.env.VUE_APP_STORE_NICK || 'psiqueasy'

                try {
                    const { data } = await api.get(`/stores/${storeNick}/affiliate/profile`, {
                        skipForceLogoutOn401: true,
                    })
                    const profile = data?.profile
                        || data?.affiliate_profile
                        || data?.affiliate_store_profile
                        || data
                        || null

                    commit('setAffiliateProfileState', {
                        profile,
                        checkedAt: now,
                    })

                    resolve(profile)
                } catch (error) {
                    const statusCode = error?.response?.status

                    // Sem perfil nessa loja: mantém estado limpo e evita chamadas repetidas em loop.
                    if ([401, 403, 404].includes(statusCode)) {
                        commit('setAffiliateProfileState', {
                            profile: null,
                            checkedAt: now,
                        })
                        return resolve(null)
                    }

                    const profileError = error?.response?.data?.errors?.profile
                    if (statusCode === 422 && Array.isArray(profileError) && profileError.length) {
                        const profileMessage = profileError[0]
                        console.log('[affiliate/profile] 422', {
                            message: profileMessage,
                            classifiedAsMissing: isAffiliateProfileMissingMessage(profileMessage),
                            classifiedAsBlocked: isAffiliateProfileBlockedMessage(profileMessage),
                        })

                        if (isAffiliateProfileMissingMessage(profileMessage)) {
                            commit('setAffiliateProfileState', {
                                profile: null,
                                checkedAt: now,
                            })
                            return resolve(null)
                        }

                        if (isAffiliateProfileBlockedMessage(profileMessage)) {
                            commit('setAffiliateProfileState', {
                                profile: {
                                    status: 'blocked',
                                    notes: profileMessage,
                                },
                                checkedAt: now,
                            })
                            return resolve(state.affiliateProfile)
                        }

                        commit('setAffiliateProfileState', {
                            profile: {
                                status: 'blocked',
                                notes: profileMessage,
                            },
                            checkedAt: now,
                        })
                        return resolve(state.affiliateProfile)
                    }

                    reject(error)
                }
            })
        },
        recoverUserData: ({ commit }, payload = {}) => {
            return new Promise(async (resolve, reject) => {
                await api.get('/profile', {
                    skipForceLogoutOn401: Boolean(payload?.skipForceLogoutOn401),
                }).then(async response => {
                    const refreshedToken = response?.data?.token
                    if (refreshedToken) {
                        persistToken(refreshedToken);
                    } else {
                        console.warn('[recoverUserData] /profile returned without token; keeping current session')
                    }

                    Cookie.remove('sysversion');
                    Cookie.set("sysversion", response.data.users?.system_info?.version, { expires: 7 });
                    if (['admin', 'merchant'].includes(response.data.users.level)) {
                        window.location.href = `${process.env.VUE_APP_FRONT_URL.replace(
                            "app",
                            "admin"
                        )}`;
                    } else {
                        const normalizedUser = normalizeAuthUser(response.data.users)
                        commit("addUser", { dataUser: normalizedUser });
                        resolve(response)
                    }
                }).catch(error => {
                    console.error('recoverUserData error', error.response?.status, error.response?.data || error)
                    reject(error)
                })
            });
        },
        userLogout: ({ commit }) => {
            return new Promise(async (resolve, reject) => {
                try {
                    await api.post('/logout')
                } catch (error) {
                    // se já estiver sem sessão, apenas registra e continua
                    console.warn('logout request failed', error?.response?.status)
                } finally {
                    clearToken()
                    commit("logout", [])
                    resolve()
                }
            });
        },
        changeUserLocale: ({ commit }, locale) => {
            commit("changeLocale", locale);
        },
        userRegister: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {

                function removeAccents(str) {

                    const with_accent = "ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝŔÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿŕ";

                    const no_accent = "AAAAAAACEEEEIIIIDNOOOOOOUUUUYRsBaaaaaaaceeeeiiiionoooooouuuuybyr";

                    let newStr = "";

                    for (let i = 0; i < str.length; i++) {
                        let replacement = false;
                        for (let a = 0; a < with_accent.length; a++) {
                            if (str.substr(i, 1) == with_accent.substr(a, 1)) {
                                newStr += no_accent.substr(a, 1);
                                replacement = true;
                                break;
                            }
                        }
                        if (replacement == false) {
                            newStr += str.substr(i, 1);
                        }
                    }
                    return newStr;
                }

                function getRandomArbitrary(min, max) {
                    return Math.random() * (max - min) + min;
                }

                if (!data.cpf && data.itin_number) {
                    data.cpf = data.itin_number
                }

                if (!data.device_name) {
                    data.device_name = 'web'
                }

                if (Object.prototype.hasOwnProperty.call(data, 'terms')) {
                    data.terms = data.terms ? 1 : 0
                }

                let fullName = removeAccents(data.fullName || '')
                fullName = fullName.replace(/[^a-zA-Zs]/g, "").toLowerCase()
                let randomNumber = getRandomArbitrary(1, 99999)
                fullName = fullName.substring(0, 13) + Math.floor(randomNumber)

                if (!data.username) {
                    data.username = fullName
                }

                await api.post('/register', data).then(response => {
                    Cookie.remove('oliveiraToken')
                    persistToken(response.data.token);
                    response.data.users.roles.map(e => {
                        e.permissions.map(el => {
                            response.data.users.roles.push({
                                action: 'read',
                                subject: el.name,
                            })
                        })
                    })
                        response.data.users.roles.push({
                            action: 'read',
                            subject: 'Public',
                        })
                        response.data.users.is_affiliate = Boolean(response.data.users.is_affiliate)
                        commit("addUser", { dataUser: response.data.users });
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.errors)
                })
            });
        },
        userEditProfile: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {
                if (data.avatar) {
                    let avatar = new FormData()
                    avatar.append('avatar', data.avatar)
                    await api.post('/profile/avatar', avatar)
                }
                const dataArray = data
                if (dataArray.avatar) {
                    delete dataArray.avatar
                }
                if (dataArray) {
                    await api.patch('/profile', dataArray).then(response => {
                        response.data.users.is_affiliate = Boolean(response.data.users.is_affiliate)
                        commit("addUser", { dataUser: response.data.users });
                        resolve(response)
                    }).catch(error => {
                        reject(error.response.data.errors)
                    })
                }
            });
        },
        forgotPassword: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {
                await api.post('/forgot-password', data).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })

            });
        },
        redefinePassword: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {
                await api.post(data.routerRedefinePsw, { password: data.password, password_confirmation: data.password_confirmation }).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })

            });
        },
        newPassword: ({ commit }, data) => {
            return new Promise(async (resolve, reject) => {
                await api.post(data.routerRedefinePsw, { password: data.password, password_confirmation: data.password_confirmation }).then(response => {
                    persistToken(response.data.token);
                    response.data.users.roles.map(e => {
                        e.permissions.map(el => {
                            response.data.users.roles.push({
                                action: 'read',
                                subject: el.name,
                            })
                        })
                    })
                    response.data.users.roles.push({
                        action: 'read',
                        subject: 'Public',
                    })
                    commit("addUser", { dataUser: response.data.users });
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })

            });
        },
        verifySignature: ({ commit }, routerConfirmeEmailAPI) => {
            return new Promise(async (resolve, reject) => {
                await api.get(routerConfirmeEmailAPI).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })

            });
        },
        confirmEmail: ({ commit }) => {
            return new Promise(async (resolve, reject) => {
                await api.post('/email/verification-notification').then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })
            });
        },
        confirmEmailAPI: ({ commit }, routerConfirmeEmailAPI) => {
            return new Promise(async (resolve, reject) => {
                await api.get(routerConfirmeEmailAPI).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })
            });
        },
        confirmEmailAPIChange: ({ commit }, routerConfirmeEmailAPI) => {
            return new Promise(async (resolve, reject) => {
                await api.get(routerConfirmeEmailAPI).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error.response.data.message)
                })
            });
        },
        oauth: ({ commit }, token) => {
            return new Promise(async (resolve, reject) => {
                await api.get(`/oauth/callback/token/${token}`).then(response => {
                    persistToken(response.data.token);
                    if (['admin', 'merchant'].includes(response.data.users.level)) {
                        window.location.href = `${process.env.VUE_APP_FRONT_URL.replace(
                            "app",
                            "admin"
                        )}`;
                    } else {
                        response.data.users.roles.map(e => {
                            e.permissions.map(el => {
                                response.data.users.roles.push({
                                    action: 'read',
                                    subject: el.name,
                                })
                            })
                        })
                        response.data.users.roles.push({
                            action: 'read',
                            subject: 'Public',
                        })
                        commit("addUser", { dataUser: response.data.users });
                        resolve(response)
                    }
                }).catch(error => {
                    reject(error.response.data.message)
                })

            });
        }
    },
    mutations: {
        addUser: (state, user) => {
            const normalizeFlag = (value) => value === true || value === 1 || value === "1"
            state.user = user.dataUser
            // novos campos retornados pelo backend (opcionais)
            state.affiliateAccount = user?.dataUser?.affiliate_account || null
            state.affiliateStoreProfiles = user?.dataUser?.affiliate_store_profiles || []
            state.affiliateProfile = user?.dataUser?.affiliate_profile || null
            state.affiliateProfileStatus = normalizeAffiliateStatus(state.affiliateProfile?.status) || null
            state.affiliateProfileCheckedAt = state.affiliateProfile ? Date.now() : null
            const profileStatusBlocked = isAffiliateStatusBlocked(state.affiliateProfileStatus)
            state.isAffiliate = Boolean(
              normalizeFlag(user?.dataUser?.is_affiliate) ||
              (state.affiliateAccount && normalizeAffiliateStatus(state.affiliateAccount.status) === AFFILIATE_STATUS_ACTIVE) ||
              normalizeAffiliateStatus(state.affiliateProfileStatus) === AFFILIATE_STATUS_ACTIVE
            )
            if (profileStatusBlocked) {
                state.isAffiliate = false
            }
        },
        setAffiliateProfileState: (state, payload = {}) => {
            const profile = payload?.profile || null
            const normalizedStatus = normalizeAffiliateStatus(profile?.status) || null
            state.affiliateProfile = profile
            state.affiliateProfileStatus = normalizedStatus
            state.affiliateProfileCheckedAt = payload?.checkedAt || Date.now()

            if (isAffiliateStatusBlocked(normalizedStatus)) {
                state.isAffiliate = false
                return
            }

            if (normalizedStatus === AFFILIATE_STATUS_ACTIVE) {
                state.isAffiliate = true
            }
        },
        logout: (state, user) => {
            state.user = user
            state.isAffiliate = false
            state.affiliateAccount = null
            state.affiliateStoreProfiles = []
            state.affiliateProfile = null
            state.affiliateProfileStatus = null
            state.affiliateProfileCheckedAt = null
        },
        changeLocale: (state, locale) => {
            state.userLocale = locale
        },
    },
}

export default authentication;
